import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEO, buildBreadcrumbSchema, buildFAQSchema } from "@/components/seo/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, X, ArrowRight, Clock, MousePointerClick, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const APPSOURCE_URL =
  "https://marketplace.microsoft.com/en-us/product/WA200010021?tab=Overview";

const faqs = [
  {
    question: "Can I add a contact from an email in Outlook Web?",
    answer:
      "Yes. In the new Outlook and Outlook on the Web (OWA), open the email, click the sender's name, then choose 'Add to contacts'. You'll need to fill in phone, title, and company manually unless you use an add-in like Email2Contact that extracts those fields from the email signature automatically.",
  },
  {
    question: "Does Outlook automatically save email senders as contacts?",
    answer:
      "No. By default, Outlook only stores names in the auto-complete cache (the suggestion list when you start typing in the To field). These are not real contacts — they aren't searchable, don't sync to your phone, and disappear if the cache is cleared. To create a real contact, you have to add it manually or use an add-in.",
  },
  {
    question: "How do I extract a contact from an email signature in Outlook?",
    answer:
      "Outlook has no built-in way to parse signatures. You either copy fields manually one by one, or install an add-in. Email2Contact reads the signature with AI and creates a complete contact (name, title, company, phone, mobile, address, website) in one click.",
  },
  {
    question: "Can I save email contacts to a shared address book?",
    answer:
      "Yes — if your organisation has a shared mailbox or a Microsoft 365 group with a shared contacts folder, you can save into it. Email2Contact supports saving to your personal Contacts folder; shared-folder support depends on your Outlook configuration.",
  },
  {
    question: "Does this work on Outlook for Mac and mobile?",
    answer:
      "Email2Contact runs in any Outlook that supports modern add-ins: Outlook on the Web, the new Outlook for Windows, classic Outlook for Windows, Outlook for Mac, and Outlook on iOS and Android.",
  },
  {
    question: "Is Email2Contact free?",
    answer:
      "Yes, you get 4 free credits when you sign up — enough to save 4 contacts and try it out. After that, plans start at €5/month or you can buy credit top-ups as you need them.",
  },
];

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "How to Add a Contact from an Email in Outlook (the 1-Click Way)",
  description:
    "Outlook makes saving contacts from emails painfully slow. Here's the manual 8-step way — and a 1-click add-in that does it for you.",
  author: { "@type": "Organization", name: "Email2Contact" },
  publisher: {
    "@type": "Organization",
    name: "Celsios GmbH",
    url: "https://email2contact.com",
  },
  datePublished: "2026-05-13",
  dateModified: "2026-05-13",
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id":
      "https://email2contact.com/blog/add-contact-from-email-outlook",
  },
};

const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "Add a contact from an email in Outlook",
  totalTime: "PT1M",
  step: [
    { "@type": "HowToStep", name: "Open the email", text: "Open the email from the sender you want to save." },
    { "@type": "HowToStep", name: "Click the sender's name", text: "Click the sender's name or avatar in the message header." },
    { "@type": "HowToStep", name: "Open contact card", text: "Click the three-dot menu and choose 'Add to contacts' (new Outlook) or 'Add to Outlook Contacts' (classic)." },
    { "@type": "HowToStep", name: "Fill in details", text: "Manually copy phone, job title, and company from the signature into the contact card." },
    { "@type": "HowToStep", name: "Save", text: "Click Save (or Create) to store the contact in your address book." },
  ],
};

const Section = ({ children, className = "", id }: { children: React.ReactNode; className?: string; id?: string }) => (
  <section id={id} className={`py-12 md:py-16 ${className}`}>
    <div className="container px-4 max-w-4xl mx-auto">{children}</div>
  </section>
);

const AddContactFromEmailOutlook = () => {
  return (
    <div className="min-h-screen flex flex-col app-surface">
      <SEO
        title="Add a Contact from an Email in Outlook — The 1-Click Way"
        description="Outlook makes saving contacts from emails painfully slow. Here's the manual 8-step way — and a 1-click add-in that does it for you. Free to try."
        path="/blog/add-contact-from-email-outlook"
        jsonLd={[
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: "Add Contact from Email in Outlook", path: "/blog/add-contact-from-email-outlook" },
          ]),
          buildFAQSchema(faqs),
          articleSchema,
          howToSchema,
        ]}
      />
      <Navbar />

      <main className="flex-grow bg-dot-grid">
        {/* HERO */}
        <Section className="pt-16 md:pt-24">
          <p className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground mb-4">
            Outlook Productivity · 4 min read
          </p>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight mb-6">
            How to Add a Contact from an Email in Outlook
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl">
            Two ways to do it: the manual Outlook process (8 clicks, ~60
            seconds) — or one click with{" "}
            <span className="text-foreground font-medium">Email2Contact</span>.
            This guide covers both, honestly.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <Button asChild size="lg">
              <a href={APPSOURCE_URL} target="_blank" rel="noopener noreferrer">
                Install free from Microsoft AppSource
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline">
              <a href="#manual-method">Skip to manual method</a>
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Works in Outlook on the Web, new Outlook, classic Desktop, Mac, iOS &amp; Android · Microsoft-certified
          </p>
        </Section>

        {/* FAST WAY */}
        <Section>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-5 w-5 text-primary" />
            <p className="text-xs font-mono uppercase tracking-[0.18em] text-primary">
              The fast way
            </p>
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mb-6">
            One click with Email2Contact
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Email2Contact is a Microsoft-certified Outlook add-in that reads
            the email signature with AI and creates a full contact in your
            address book — no copy-paste, no missed fields.
          </p>

          <ol className="space-y-4 mb-10">
            {[
              "Open any email in Outlook.",
              'Click the "Email2Contact" button in the ribbon.',
              "Done — name, title, company, phone, mobile, address and website are saved as a contact.",
            ].map((text, i) => (
              <li key={i} className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold">
                  {i + 1}
                </div>
                <p className="pt-1 text-base md:text-lg">{text}</p>
              </li>
            ))}
          </ol>

          <Card className="p-6 bg-primary/5 border-primary/20">
            <p className="font-medium mb-3">
              Why use an add-in instead of copy-pasting?
            </p>
            <ul className="space-y-2 text-muted-foreground text-sm md:text-base">
              <li className="flex gap-2"><Check className="h-5 w-5 text-primary flex-shrink-0" /> Captures fields Outlook ignores: job title, mobile, address, website.</li>
              <li className="flex gap-2"><Check className="h-5 w-5 text-primary flex-shrink-0" /> Handles signatures in any language and format.</li>
              <li className="flex gap-2"><Check className="h-5 w-5 text-primary flex-shrink-0" /> 100% EU-hosted AI processing, GDPR-compliant, zero data retention.</li>
            </ul>
            <Button asChild className="mt-6">
              <a href={APPSOURCE_URL} target="_blank" rel="noopener noreferrer">
                Get it free <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </Card>
        </Section>

        {/* MANUAL METHOD */}
        <Section id="manual-method" className="border-t border-border/50">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <p className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">
              The manual way
            </p>
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mb-6">
            Outlook's built-in method
          </h2>
          <p className="text-lg text-muted-foreground mb-10">
            If you only need to save the occasional contact, Outlook's built-in
            workflow works — it's just slow. Here's how it works on each
            platform.
          </p>

          {/* New Outlook / OWA */}
          <h3 className="font-display text-2xl font-semibold mb-4">
            New Outlook &amp; Outlook on the Web (OWA)
          </h3>
          <ol className="list-decimal pl-6 space-y-3 mb-10 text-base md:text-lg marker:text-muted-foreground">
            <li>Open the email from the person you want to save.</li>
            <li>Click the sender's <strong>name or avatar</strong> in the message header — a contact card appears.</li>
            <li>Click the <strong>three-dot menu</strong> on the card.</li>
            <li>Choose <strong>"Add to contacts"</strong>.</li>
            <li>Manually fill in phone, job title, company, address — copy each field from the signature into the form.</li>
            <li>Click <strong>Create</strong> to save.</li>
          </ol>

          {/* Classic Outlook */}
          <h3 className="font-display text-2xl font-semibold mb-4">
            Classic Outlook for Windows (Desktop)
          </h3>
          <ol className="list-decimal pl-6 space-y-3 mb-6 text-base md:text-lg marker:text-muted-foreground">
            <li>Open the email.</li>
            <li><strong>Right-click</strong> the sender's name in the From field.</li>
            <li>Choose <strong>"Add to Outlook Contacts"</strong>.</li>
            <li>A contact form opens, pre-filled with name and email only.</li>
            <li>Switch back to the email, copy the phone number from the signature.</li>
            <li>Paste it into the Phone field of the contact form.</li>
            <li>Repeat for job title, company, mobile, address, website.</li>
            <li>Click <strong>Save &amp; Close</strong>.</li>
          </ol>
          <p className="text-sm text-muted-foreground italic">
            That's 8 clicks plus 5–6 copy-paste cycles — about 60 seconds per
            contact, longer if you keep hitting the wrong field.
          </p>
        </Section>

        {/* COMPARISON */}
        <Section className="border-t border-border/50">
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mb-8 text-center">
            Side-by-side comparison
          </h2>

          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 font-semibold"></th>
                  <th className="px-4 py-3 font-semibold">Manual Outlook</th>
                  <th className="px-4 py-3 font-semibold text-primary">Email2Contact</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {[
                  ["Clicks per contact", "8", "1"],
                  ["Time per contact", "~60 sec", "~2 sec"],
                  ["Extracts signature data", false, true],
                  ["Captures phone, mobile, title, company", false, true],
                  ["Captures address &amp; website", false, true],
                  ["Works on Outlook mobile", false, true],
                  ["Cost", "Free", "Free to start (4 credits)"],
                ].map(([label, manual, e2c], i) => (
                  <tr key={i}>
                    <td className="px-4 py-3 font-medium">{label as string}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {typeof manual === "boolean" ? (
                        manual ? <Check className="h-5 w-5 text-primary" /> : <X className="h-5 w-5 text-muted-foreground" />
                      ) : manual}
                    </td>
                    <td className="px-4 py-3">
                      {typeof e2c === "boolean" ? (
                        e2c ? <Check className="h-5 w-5 text-primary" /> : <X className="h-5 w-5 text-muted-foreground" />
                      ) : e2c}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* EXTRACT CLUSTER — targets "extract contacts from emails" / "email signature parser" */}
        <Section className="border-t border-border/50">
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mb-6">
            How to extract contacts from Outlook emails
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            "Extracting" a contact means pulling structured data — name, job
            title, company, phone, mobile, address, website — out of the
            unstructured text inside an email or its signature, then writing
            it into your address book. There are three ways to do it in
            Outlook.
          </p>

          <div className="space-y-8">
            <div>
              <h3 className="font-display text-xl font-semibold mb-2">
                1. Manual copy-paste (what most people do)
              </h3>
              <p className="text-muted-foreground">
                Open the email, highlight each field in the signature, paste
                into a new contact form, repeat. Works for one or two
                contacts a week. Painful at scale and prone to typos.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl font-semibold mb-2">
                2. Email signature parser add-in (the fast way)
              </h3>
              <p className="text-muted-foreground">
                A signature parser uses AI to read the signature block,
                identify each field, and create a complete contact in one
                click. Email2Contact is built exactly for this — it handles
                multi-line signatures, international formats, and mobile
                vs. office phone numbers automatically.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl font-semibold mb-2">
                3. Bulk extract via export (developer route)
              </h3>
              <p className="text-muted-foreground">
                For thousands of historical emails, export your mailbox to
                PST or MBOX and run a script (Python's <code>email</code>{" "}
                module, or a tool like <em>Email Address Extractor</em>) to
                pull addresses. You'll get email addresses only — names,
                phones and titles still need a parser to enrich them.
              </p>
            </div>
          </div>
        </Section>

        {/* PITFALLS */}
        <Section className="border-t border-border/50">
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mb-8">
            Common pitfalls (and how to avoid them)
          </h2>

          <div className="space-y-8">
            <div>
              <h3 className="font-display text-xl font-semibold mb-2">
                Why doesn't Outlook auto-save email senders as contacts?
              </h3>
              <p className="text-muted-foreground">
                Microsoft removed automatic contact creation years ago for
                privacy and inbox-noise reasons. The "suggested contacts" cache
                you see in the To field is just an autocomplete index — those
                entries aren't searchable, don't sync to your phone, and vanish
                if the cache is cleared. Real contacts must be created
                deliberately.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl font-semibold mb-2">
                How to add multiple contacts at once from emails
              </h3>
              <p className="text-muted-foreground">
                Outlook has no native bulk-add. With Email2Contact you can
                process emails one by one in seconds; for a true bulk import,
                export contacts to CSV from another tool and import via
                Outlook's <em>People → Manage → Import contacts</em>.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl font-semibold mb-2">
                Saving contacts to a shared mailbox or address book
              </h3>
              <p className="text-muted-foreground">
                If your team uses a shared mailbox, contacts saved there are
                visible to anyone with access. Open the shared mailbox in
                Outlook, switch to its People folder, and add the contact
                there. Email2Contact saves to your personal Contacts folder
                today; shared-folder targeting is on the roadmap.
              </p>
            </div>
          </div>
        </Section>

        {/* FAQ */}
        <Section className="border-t border-border/50">
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mb-8">
            FAQ
          </h2>
          <div className="space-y-6">
            {faqs.map((f, i) => (
              <div key={i}>
                <h3 className="font-semibold text-lg mb-2">{f.question}</h3>
                <p className="text-muted-foreground">{f.answer}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* FINAL CTA */}
        <Section className="border-t border-border/50">
          <Card className="p-8 md:p-12 text-center bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-primary/20">
            <MousePointerClick className="h-10 w-10 text-primary mx-auto mb-4" />
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mb-4">
              Stop copy-pasting contacts
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Install Email2Contact for free and save your next contact in one
              click. 4 free credits, no credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild size="lg">
                <a href={APPSOURCE_URL} target="_blank" rel="noopener noreferrer">
                  Get it on Microsoft AppSource
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/pricing">See pricing</Link>
              </Button>
            </div>
          </Card>
        </Section>
      </main>

      <Footer />
    </div>
  );
};

export default AddContactFromEmailOutlook;
